import React, { ReactElement } from 'react'
import { StyleSheet } from 'react-native'
import { Defs, LinearGradient, Stop, Svg } from 'react-native-svg'
import {
  VictoryChart,
  VictoryVoronoiContainer,
  VictoryLine,
  VictoryTooltip,
  VictoryScatter,
  InterpolationPropType,
  VictoryArea,
  VictoryGroup,
} from 'victory'

import { Color, Radius, Spacing } from '../../style'

import ChartDot from './dot'
import ChartFlyover from './flyover'

const style = StyleSheet.create({
  defs: {
    height: 0,
  },
})

interface DataPoint {
  x: number | string
  y: number
}

interface Line {
  data: DataPoint[]
  interpolation?: InterpolationPropType
  lineColor?: string
  dot?: boolean | ReactElement
  dotColor?: string
  background?: string | string[]
  tooltip?: boolean | ReactElement
  valueLabel?: (value: number | string, axis: 'x' | 'y') => string
}

interface Axis {
  grid?: boolean
  min?: number
  max?: number
  label?: string
}

export interface Props {
  data: Line[]
  axis?: {
    x?: Axis
    y?: Axis
  }
  width?: number
  height?: number
  animationDuration?: number
}
const ChartLine = (props: Props) => {
  const { data, axis, width, height, animationDuration = 1000 } = props
  const xAxis = { ...axis?.x }
  const yAxis = { grid: true, ...axis?.y }

  // const labelForValue = (value: number | string) => {
  //   if (typeof value === 'string') return value
  //   else return `${Math.round(value as number)}`
  // }

  const gradients = data?.reduce((acc, line) => {
    if (Array.isArray(line.background)) {
      acc.push(line.background)
    }
    return acc
  }, [] as string[][])

  return (
    <>
      {!!gradients.length && (
        <Svg style={style.defs}>
          <Defs>
            {gradients.map((colors, index) => (
              <LinearGradient
                key={`${index}-${colors.join('-')}`}
                id={`gradient-${colors.join('-')}`}
                gradientTransform="rotate(90)"
              >
                {colors?.map((color, colorIndex) => {
                  return (
                    <Stop
                      key={`${color}-${colorIndex}`}
                      offset={`${(colorIndex / (colors.length - 1)) * 100}%`}
                      stopColor={color}
                    />
                  )
                })}
              </LinearGradient>
            ))}
          </Defs>
        </Svg>
      )}
      <VictoryChart
        containerComponent={<VictoryVoronoiContainer responsive={false} />}
        minDomain={{ x: xAxis.min, y: yAxis.min }}
        maxDomain={{ x: xAxis.max, y: yAxis.max }}
        width={width}
        height={height}
      >
        {/* FIXME: Axis has typing errors */}
        {/* <VictoryAxis
          crossAxis
          style={{
            ...(xAxis.grid ? { grid: { stroke: Color.calmGrey, strokeDasharray: [8, 2] } } : undefined),
            axis: { stroke: Color.premiumBlack, strokeWidth: 1 },
            axisLabel: { fontFamily: Font.light, fontSize: 10, fill: Color.premiumBlack, padding: 30 },
            tickLabels: { fontFamily: Font.light, fontSize: 10, fill: Color.premiumBlack },
          }}
          tickFormat={labelForValue}
          label={xAxis.label}
        />
        <VictoryAxis
          crossAxis
          dependentAxis
          style={{
            ...(yAxis.grid ? { grid: { stroke: Color.calmGrey, strokeDasharray: [8, 2] } } : undefined),
            axis: { stroke: Color.premiumBlack, strokeWidth: 1 },
            axisLabel: { fontFamily: Font.light, fontSize: 10, fill: Color.premiumBlack, padding: 30 },
            tickLabels: { fontFamily: Font.light, fontSize: 10, fill: Color.premiumBlack },
          }}
          tickFormat={labelForValue}
          label={yAxis.label}
        /> */}
        {data.map((line, index) => {
          const { data, interpolation, lineColor, dot, dotColor, background, tooltip = true, valueLabel } = line

          return (
            <VictoryGroup
              key={`line-${index}`}
              animate={{
                onLoad: {
                  duration: 0,
                },
                onExit: {
                  duration: animationDuration,
                },
              }}
            >
              {background && (
                <VictoryArea
                  interpolation={interpolation}
                  style={{
                    data: { fill: Array.isArray(background) ? `url(#gradient-${background.join('-')})` : background },
                  }}
                  data={data}
                />
              )}
              <VictoryLine
                interpolation={interpolation}
                data={data}
                style={{
                  data: { stroke: lineColor || Color.premiumBlack, strokeLinecap: 'round', strokeLinejoin: 'round' },
                }}
                labels={
                  tooltip !== false
                    ? ({ datum }) => [
                        axis?.x?.label || 'X value',
                        valueLabel ? valueLabel(datum.x, 'x') : `${datum.x}`,
                        axis?.y?.label || 'Y value',
                        valueLabel ? valueLabel(datum.y, 'y') : `${datum.y}`,
                      ]
                    : undefined
                }
                labelComponent={
                  tooltip === true ? (
                    <VictoryTooltip
                      cornerRadius={Radius.M}
                      centerOffset={{ y: -Spacing.XS }}
                      pointerLength={4}
                      pointerWidth={20}
                      flyoutPadding={{ top: Spacing.XS, bottom: Spacing.XS, left: Spacing.S, right: Spacing.S }}
                      flyoutStyle={{
                        stroke: Color.calmGrey,
                        fill: Color.simplyWhite,
                      }}
                      labelComponent={<ChartFlyover />}
                    />
                  ) : tooltip !== false ? (
                    tooltip
                  ) : undefined
                }
              />
              {dot && <VictoryScatter data={data} dataComponent={dot === true ? <ChartDot color={dotColor} /> : dot} />}
            </VictoryGroup>
          )
        })}
      </VictoryChart>
    </>
  )
}

export default ChartLine
